import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { openModal, closeModal } from 'actions/modal';
import { MODAL_FORM } from 'constants/form';
import { postRegionPayoutBulk } from 'actions/region-payout';
import { DEFAULT_CURRENCY, ratioLabel } from './constants';
import { cellKey, fmtDate, windowsOverlap, bulkCellValue, notify } from './utils';

// Owns the bulk-create flow: per-cell amount overrides, the common validity window
// applied to every created row, validation, the confirm modal and the batch POST.
// The screen passes in the current selection + derived grid data and the refetch fn.
export const useBulkCreate = ({
  activeRegion,
  course,
  classRatio,
  cellData,
  windowsForCell,
  visibleGradeRanges,
  visibleTiers,
  fetchPayouts,
}) => {
  const dispatch = useDispatch();

  const [bulkValues, setBulkValues] = useState({});
  const [bulkErrors, setBulkErrors] = useState({});
  const [bulkStart, setBulkStart] = useState('');
  const [bulkEnd, setBulkEnd] = useState('');
  const [bulkDateError, setBulkDateError] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const resetBulk = useCallback(() => {
    setBulkValues({});
    setBulkErrors({});
    setBulkDateError('');
    // Clear the common dates too, so a valid-from typed for one region/course/ratio
    // does not silently carry into the next.
    setBulkStart('');
    setBulkEnd('');
  }, []);

  const setBulkValue = (key, value) => {
    setBulkValues(prev => ({ ...prev, [key]: value }));
    setBulkErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const onBulkStart = value => {
    setBulkStart(value);
    setBulkDateError('');
  };

  const onBulkEnd = value => {
    setBulkEnd(value);
    setBulkDateError('');
  };

  const runBulkCreate = async entries => {
    setBulkSubmitting(true);
    // One request for the whole grid instead of a POST per cell. Entries keep the same
    // order we send them in, so the server can report per-cell collisions back by index.
    const payload = entries.map(entry => {
      const body = {
        region: activeRegion,
        course,
        grade_range: entry.grade,
        enrollment_type: entry.tier,
        class_ratio: classRatio,
        fee_per_class: entry.fee,
        currency: DEFAULT_CURRENCY,
        validity_start: bulkStart,
      };
      if (bulkEnd) body.validity_end = bulkEnd;
      return body;
    });
    const res = await postRegionPayoutBulk(payload);
    setBulkSubmitting(false);
    dispatch(closeModal());

    if (!res.ok) {
      // Whole request failed (network / malformed batch) — nothing was created.
      notify(dispatch, res.error || 'Failed to create windows.');
      return;
    }

    // Map each per-entry failure back onto its cell via the index we sent, so the grid
    // highlights just the colliding cells — same UX as the old per-POST loop.
    const failures = {};
    res.failures.forEach(f => {
      const entry = entries[f.index];
      if (entry) failures[entry.key] = f.error;
    });
    setBulkErrors(failures);
    // getPayouts always refetches, so an explicit invalidate is redundant here.
    fetchPayouts();

    const created = res.created.length;
    if (Object.keys(failures).length) {
      notify(
        dispatch,
        `Created ${created}/${entries.length}. Some windows collided — see highlighted cells.`,
      );
    } else {
      setBulkValues({});
      notify(dispatch, `Created ${created} payout window${created > 1 ? 's' : ''}.`);
    }
  };

  const submitBulk = () => {
    if (!bulkStart) {
      setBulkDateError('Valid-from date is required.');
      return;
    }
    if (bulkEnd && bulkEnd <= bulkStart) {
      setBulkDateError('Valid-until must be after valid-from.');
      return;
    }
    setBulkDateError('');

    const entries = [];
    const errors = {};
    visibleGradeRanges.forEach(grade => {
      visibleTiers.forEach(tier => {
        const key = cellKey(activeRegion, grade, tier);
        const entry = cellData[key];
        const current = entry ? entry.row.fee_per_class : null;
        const raw = bulkCellValue(bulkValues, key, current).trim();
        if (raw === '') return; // cleared = excluded from the bulk create
        const fee = Number(raw);
        if (!Number.isInteger(fee) || fee <= 0) {
          errors[key] = 'Enter a positive whole number.';
          return;
        }
        const collide = windowsForCell(activeRegion, grade, tier).find(row =>
          windowsOverlap(bulkStart, bulkEnd, row.validity_start, row.validity_end),
        );
        if (collide) {
          errors[key] = `Overlaps ${fmtDate(collide.validity_start)} → ${fmtDate(
            collide.validity_end,
          ) || 'open'}`;
          return;
        }
        entries.push({ key, grade, tier, fee });
      });
    });

    setBulkErrors(errors);
    if (Object.keys(errors).length) {
      notify(dispatch, 'Fix the highlighted cells before creating.');
      return;
    }
    if (!entries.length) {
      setBulkDateError('Enter at least one amount to create.');
      return;
    }

    dispatch(
      openModal(
        MODAL_FORM.REGION_PAYOUT_BULK_CONFIRM_MODAL,
        { size: 'custom', width: '480px', height: 'fit-content' },
        {
          count: entries.length,
          region: activeRegion,
          ratio: ratioLabel(classRatio),
          currency: DEFAULT_CURRENCY,
          validityStart: fmtDate(bulkStart),
          validityEnd: bulkEnd ? fmtDate(bulkEnd) : null,
          entries: entries.map(e => ({ grade: e.grade, tier: e.tier, fee: e.fee })),
          onConfirm: () => runBulkCreate(entries),
        },
      ),
    );
  };

  return {
    bulkValues,
    bulkErrors,
    bulkStart,
    bulkEnd,
    bulkDateError,
    bulkSubmitting,
    resetBulk,
    setBulkValue,
    onBulkStart,
    onBulkEnd,
    submitBulk,
  };
};
